import { v4 as uuid } from "uuid";

import { useRouter } from "@medbillai/navigation/router";
import {
  GET_HELP_PROMPT_CASE_TYPE_MAP,
  type GetHelpPromptKey,
} from "@medbillai/schemas";
import { issuePreviewUrl } from "@medbillai/utils";

import {
  encodePreviewConfig,
  type HealthRecordRef,
} from "../screens/case/hooks/useCaseScreenParams";
import { createGetHelpPromptEvent } from "../screens/case/utils/createPreviewEvents";
import { analyticsService } from "./analytics";
import { isBenefitsTargeted, useAuth } from "./auth";
import { useCaseStore } from "./caseStore";

export const G_BUTTON_SOURCE = "tab_bar_granted_button";
export const GET_STARTED_SOURCE = "home_getting_started_widget";
export const CASES_LIST_NEW_CASE_BUTTON_SOURCE = "cases_list_new_case_button";
export const CASES_PANEL_NEW_CASE_BUTTON_SOURCE = "cases_panel_new_case_button";
export const WEB_SIDEBAR_ASK_GRANTED_SOURCE = "web_sidebar_ask_granted";

export function getPreviewConfig({
  caseType,
  title,
  userFeatures,
  source,
}: {
  caseType:
    | "BENEFITS_QUESTION"
    | "OTHER"
    | "OPEN_ENROLLMENT"
    | "BILL_ISSUE"
    | "BILL_ISSUE_V2";
  title: string;
  userFeatures: string[] | null | undefined;
  source: string;
}) {
  const isUserBenefitsTargeted = isBenefitsTargeted(userFeatures);

  let messageId: GetHelpPromptKey = `get_help_prompt_${caseType}`;
  if (
    [
      G_BUTTON_SOURCE,
      GET_STARTED_SOURCE,
      CASES_LIST_NEW_CASE_BUTTON_SOURCE,
      CASES_PANEL_NEW_CASE_BUTTON_SOURCE,
      WEB_SIDEBAR_ASK_GRANTED_SOURCE,
    ].includes(source)
  ) {
    if (isUserBenefitsTargeted) {
      messageId = "get_help_prompt_TARGETED_BENEFITS_QUESTION";
    } else {
      messageId = "get_help_prompt_get_started";
    }
  }
  const message = GET_HELP_PROMPT_CASE_TYPE_MAP[messageId];

  const id = uuid();
  const events = [createGetHelpPromptEvent(messageId)];

  // TODO: remove the ternary once we release this to production and we
  // don't have to support old versions of the app.
  // https://linear.app/medbillai/issue/GRO-117/remove-bill-upload-ui-feature-flag
  const previewCaseType =
    caseType === "BILL_ISSUE_V2" ? "BILL_ISSUE" : caseType;

  return {
    config: {
      id,
      caseType: previewCaseType,
      title,
      events,
    },
    startingMessage: message,
  };
}

export const usePushGetHelp = () => {
  const { push, replace } = useRouter();
  const setPreviewData = useCaseStore(state => state.setPreviewData);
  const { user } = useAuth();

  const pushGetHelp = ({
    caseType,
    title,
    source,
    replaceRoute = false,
    healthRecord,
  }: {
    caseType:
      | "BENEFITS_QUESTION"
      | "OTHER"
      | "OPEN_ENROLLMENT"
      | "BILL_ISSUE"
      | "BILL_ISSUE_V2";
    title: string;
    source: string;
    replaceRoute?: boolean;
    healthRecord?: HealthRecordRef;
  }) => {
    analyticsService.trackEvent("CaseIntakeStarted", {
      source,
    });

    const { config, startingMessage } = getPreviewConfig({
      caseType,
      title,
      source,
      userFeatures: user?.userSegmentationData?.features,
    });

    // Track pill prompts order when they are shown
    if (startingMessage.options && startingMessage.options.length > 0) {
      analyticsService.trackEvent("PillPromptsShown", {
        pillPromptsOrder: startingMessage.options,
        caseType,
      });
    }

    const { id, events } = config;

    // Save data to store
    setPreviewData(id, { events });

    // Navigate to preview case
    (replaceRoute ? replace : push)(
      issuePreviewUrl({
        config: encodePreviewConfig({
          ...config,
          events: config.events,
          healthRecord,
        }),
      }),
    );
  };

  return { pushGetHelp };
};
